// @givmo/compliance — what each document is used for (CCM-R1-INTAKE (a); frames 06, 12). Pure
// functions over the Filings composition and each library filing's own documents; no React, no reads.
//
// A document is "used for" a filing where that filing's record names it: a year's documents (the
// cycle's, by role), the documents on the obligation row (its own read, by role), and an own
// filing's periods (the confirmation, the filing as sent, the proof). Nothing here says whether a
// document is the right one or what it contains: it says only what the organization's record points
// at. A document no record names is used for nothing yet, and says so with a dash.

import type { ObligationArtifact } from './compliance-decode';
import type { CycleDocumentRole, OwnFilingCycle, RenewalCycle } from './compliance-filings-decode';
import { filingKey, type FilingRow } from './compliance-filings-model';
import { knownRole } from './compliance-panel-model';

/** One filing a document is used for, and the role it plays there. */
export interface DocumentUse {
  filingKey: string;
  title: string;
  role: CycleDocumentRole;
}

export interface DocumentUsesInput {
  rows: readonly FilingRow[];
  cycles: readonly RenewalCycle[];
  /** Each library filing's own documents, by obligation id. */
  obligationDocuments: ReadonlyMap<string, readonly ObligationArtifact[]>;
  /** Each own filing's periods, by filing id. */
  ownFilingCycles: ReadonlyMap<string, readonly OwnFilingCycle[]>;
}

/** "Maryland · Annual report", as every Compliance screen names a filing. */
function titleOf(row: FilingRow): string {
  return row.subtitle !== undefined ? `${row.headline} · ${row.subtitle}` : row.headline;
}

/** Document id → the filings it is used for, each once per role. */
export function documentUses(input: DocumentUsesInput): Map<string, DocumentUse[]> {
  const uses = new Map<string, DocumentUse[]>();
  const add = (artifactId: string | undefined, row: FilingRow, role: CycleDocumentRole) => {
    if (artifactId === undefined || artifactId.length === 0) return;
    const key = filingKey(row.ref);
    const known = uses.get(artifactId) ?? [];
    if (known.some((use) => use.filingKey === key && use.role === role)) return;
    uses.set(artifactId, [...known, { filingKey: key, title: titleOf(row), role }]);
  };
  for (const row of input.rows) {
    if (row.ref.kind === 'library') {
      for (const cycle of input.cycles) {
        if (cycle.obligationId !== row.ref.id) continue;
        for (const document of cycle.documents) add(document.artifactId, row, document.role);
      }
      for (const document of input.obligationDocuments.get(row.ref.id) ?? []) {
        const role = knownRole(document.referenceRole);
        if (document.isCurrent && role !== undefined) add(document.artifactId, row, role);
      }
    } else {
      for (const period of input.ownFilingCycles.get(row.ref.id) ?? []) {
        add(period.filedCopyArtifactId, row, 'filed_copy');
        add(period.confirmationArtifactId, row, 'confirmation');
        add(period.submissionProofArtifactId, row, 'submission_proof');
      }
    }
  }
  return uses;
}
